<script setup>
import MatchGroupHeader from '@/components/Match/MatchGroupHeader.vue';
import MatchListItem from '@/components/Match/MatchListItem.vue';
import PageTitleSection from '@/components/PageTitleSection.vue';
import { useFixturesStore } from '@/stores/fixtures';
import { storeToRefs } from 'pinia';
import { computed, ref, onMounted, watch } from 'vue';
import { useRoute } from 'vue-router';
import mainMarkets from '@/mainMarkets'
import { useI18n } from 'vue-i18n';
import { useVisibleItems } from '@/composables/useVisibleItems';

const { t, te } = useI18n()
const fixtures = useFixturesStore()
const { getCompetition, eventsByCompetition } = storeToRefs(fixtures)
const selectionsMap = ref({})
const route = useRoute()
const ci = computed(() => route.params.ci)
const si = computed(() => route.params.si)
const eventsByDate = computed(() => {
    const groups = {};
    for (const key in eventsByCompetition.value(ci.value)) {
        const event = eventsByCompetition.value(ci.value)[key];
        if (event.o == 'true') {
            if (!groups['Outright']) {
                groups['Outright'] = [];
            }
            groups['Outright'].push(event);
        }  else{
            const { t } = event;

            const date = new Date(t);
            const dateString = date.toISOString().split('T')[0];

            if (!groups[dateString]) {
                groups[dateString] = [];
            }

            groups[dateString].push(event);
        }
    }

    for (const date in groups) {
        groups[date].sort((a, b) => new Date(a.t) - new Date(b.t));
    }

    // Sort by date 
    const sortedGroups = {};
    Object.keys(groups)
        .sort((a, b) => new Date(a) - new Date(b))
        .forEach(date => {
            sortedGroups[date] = groups[date];
        });

    return sortedGroups;
});

const preMarketId = computed(() => {
  return mainMarkets[si.value]?.[0] || null
})

const loadSelections = async (ids) => {
  if (!ids.length) return

  const marketId = mainMarkets[si.value]?.[0]
  const markets = marketId ? [marketId] : undefined

  const res = await fixtures.fetchSelections(ids, "pre", markets)
  for (const ev of res) {
    selectionsMap.value[ev.fi] = ev.m || []
  }
}

const { observe, reset } = useVisibleItems(loadSelections, { selector: '.event-item' })

onMounted(() => {
  observe()
})

// Re-observe when the rendered events change (e.g. navigating between leagues
// while the component instance is reused, so onMounted does not run again).
watch(eventsByDate, () => {
  reset()
  observe()
})

let competitionName = te(`competitions.${ci.value}`)
  ? t(`competitions.${ci.value}`)
  : getCompetition.value(ci.value)?.cn
</script>
<template>
  <div class="flex flex-1">
    <div class="flex-1">
      <PageTitleSection :title="competitionName"/>
      <div class="bg-[#383838] rounded">
        <div v-for="events, date in eventsByDate">
          <MatchGroupHeader :title="date" :sport="si" />

          <ul class="list-none divide-y divide-neutral-600/50">
            <li 
              v-for="event in events"
              :key="event.i"
              class="event-item"
              :data-i="event.i"
            >
              <MatchListItem
                :event="event"
                :selections="selectionsMap[event.i] || []"
                :market="preMarketId" 
              />
            </li>
          </ul>
        </div>
      </div>
    </div>
  </div>
</template>
